import React from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { ProductCardTextAlignmentOption } from 'types/graphql';
import { ProductCard } from 'src/dispensary-admin/components/product-card';
import { PreviewData } from '../components/preview';
import { usePreview } from '../components/preview/use-preview';

type ProductCardPreviewProps = {
  data: PreviewData;
  showMobileAppPreview?: boolean;
  showAdvancedCustomization?: boolean;
  // Independent of showAdvancedCustomization (which is coupled to the mobile app section) --
  // strain type pill colors apply to the web marketplace card too, so they're gated on the
  // ecomm.app.advanced-customization flag alone.
  showStrainTypeColors?: boolean;
  /** Hides the desktop Card View / List View figures, showing only the Mobile App Card View
   *  figure -- used alongside ProductCardsFormProps.mobileOnly. */
  mobileOnly?: boolean;
};

export const ProductCardPreview = ({
  data,
  showMobileAppPreview = false,
  showAdvancedCustomization = false,
  showStrainTypeColors = false,
  mobileOnly = false,
}: ProductCardPreviewProps): JSX.Element => {
  const { textAlignment, showProductWeight, mobileAppDisplayOptions } = data.productCards;
  // Without an explicit theme, ProductCard's IconButton (the mobile "add to cart" button) falls
  // back to whatever ambient theme happens to be in context above this preview -- not the
  // dispensary's actual configured "Buttons and Links" color. `usePreview` is the same
  // data-to-theme derivation Theme's own preview uses, so the mobile card preview's button
  // matches what actually renders on the mobile app.
  const { theme } = usePreview(data);

  // Same on/off surfacing rule as colorOverrides below -- don't keep rendering a color that was
  // saved while the flag was on after it's toggled off.
  const strainTypes = showStrainTypeColors ? data.colors.strainTypes : undefined;

  // Only surface the custom colors (and the other advanced-customization-only options) to the
  // rendered card while the flag is on -- explicitly overriding with undefined (rather than
  // omitting the keys) when it's off, so a value saved while the flag was on doesn't keep
  // rendering/appearing after it's toggled off. Keeps the preview an accurate reflection of what
  // a dispensary actually sees.
  const advancedCustomizationOverrides = showAdvancedCustomization
    ? {
        backgroundColor: mobileAppDisplayOptions.backgroundColor,
        brandColor: mobileAppDisplayOptions.brandColor,
        potencyColor: mobileAppDisplayOptions.potencyColor,
        potencyDisplay: mobileAppDisplayOptions.potencyDisplay,
        priceColor: mobileAppDisplayOptions.priceColor,
        productNameColor: mobileAppDisplayOptions.productNameColor,
        strainTypeDisplay: mobileAppDisplayOptions.strainTypeDisplay,
        strikethroughPriceColor: mobileAppDisplayOptions.strikethroughPriceColor,
        weightColor: mobileAppDisplayOptions.weightColor,
      }
    : {
        backgroundColor: undefined,
        brandColor: undefined,
        potencyColor: undefined,
        potencyDisplay: undefined,
        priceColor: undefined,
        productNameColor: undefined,
        strainTypeDisplay: undefined,
        strikethroughPriceColor: undefined,
        weightColor: undefined,
      };

  let cardImage = '';

  if (textAlignment === ProductCardTextAlignmentOption.center) {
    cardImage = showProductWeight ? 'card-preview-weight-center.png' : 'card-preview-no-weight-center.png';
  } else {
    cardImage = showProductWeight ? 'card-preview-weight-v2.png' : 'card-preview-no-weight.png';
  }

  return (
    <Container>
      {!mobileOnly && (
        <>
          <figure>
            <Caption>Card View</Caption>
            <Image src={`https://images.dutchie.com/product-cards/${cardImage}`} alt='Card View Preview' width={184} />
          </figure>
          <figure>
            <Caption>List View</Caption>
            <Image
              src='https://images.dutchie.com/product-cards/list-preview-v2.png'
              alt='List View Preview'
              width={346}
            />
          </figure>
        </>
      )}

      {showMobileAppPreview && (
        <figure>
          <Caption>Mobile App Card View</Caption>
          <MobilePreview>
            <MobileCard>
              <ThemeProvider theme={theme}>
                <ProductCard
                  isFullHeight={false}
                  // Renders the actual mobile card layout (add-to-cart icon button, no desktop
                  // "% off" discounted-price row) instead of the desktop layout this was
                  // defaulting to -- this figure is titled "Mobile App Card View", so it should
                  // reflect what actually renders on the mobile app, not the desktop card.
                  asMobileApp
                  strainTypes={strainTypes}
                  product={{
                    Name: 'Blue Dream',
                    Prices: [35],
                    Options: ['1g'],
                    brand: { name: 'Dutchie' },
                    type: 'Flower Collection',
                    strainType: 'Hybrid',
                    effects: ['Relaxed'],
                    specialNames: ['Buy More Save More', 'Member Deal'],
                    thcContent: { unit: 'PERCENTAGE', range: [22.1] },
                    cbdContent: { unit: 'PERCENTAGE', range: [0.25] },
                  }}
                  displayOptions={{
                    ...mobileAppDisplayOptions,
                    ...advancedCustomizationOverrides,
                    showProductWeight: mobileAppDisplayOptions.showProductWeight ?? showProductWeight,
                  }}
                />
              </ThemeProvider>
            </MobileCard>
          </MobilePreview>
        </figure>
      )}
    </Container>
  );
};

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const Image = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
`;

const Caption = styled.figcaption`
  font-size: 12px;
  line-height: ${18 / 12};
  margin-bottom: 8px;
  text-align: center;
`;

// No padding around MobileCard -- the whole point of this frame is to preview the mobile card's
// own "Card Background" color, so that background needs to sit flush against this frame's
// rounded corners rather than floating inside a padded gutter with the frame's own #f5f8fa
// background peeking around every edge. `overflow: hidden` clips the card to this frame's
// border-radius even if the card's own border-radius (driven by the customization's Style/radius
// setting, not this preview) doesn't exactly match.
const MobilePreview = styled.div`
  align-items: center;
  background: #f5f8fa;
  border: 1px solid #e3e7e9;
  border-radius: 20px;
  display: flex;
  justify-content: center;
  overflow: hidden;
  width: 100%;
`;

const MobileCard = styled.div`
  width: 100%;
`;
